// Shared, pure resolver for the aptitude section score columns/cards.
//
// An aptitude assessment only carries the sections picked at creation (a section
// with zero selected topics is never generated). admin-node still returns all
// three keys on `aptitudeScores`, filling the unselected ones with the string
// "-" (models/Assessment.js — `getPercentage` / `findCategory`), so the UI used
// to show a "Critical Reasoning: -" column for an assessment that never had a
// critical reasoning section.
//
// A section counts as present only when a real numeric percentage exists for it.
// Same shape as roleBasedColumns.js so both tables stay consistent.

// Canonical aptitude score keys → column labels, in display order (Overall is
// rendered separately by each consumer). Labels match the existing table
// headers; the report drawer keeps its own card titles.
const APTITUDE_SECTION_LABELS = {
  criticalReasoningPercentage: 'Critical Reasoning',
  quantitativePercentage: 'Quantitative',
  logicalReasoningPercentage: 'Logical Reasoning',
}

const APTITUDE_SECTION_ORDER = Object.keys(APTITUDE_SECTION_LABELS)

// A score is real only if it is a number (or a numeric string). Guards against
// "-" (section not in the assessment) and status strings such as "Pending",
// both of which `Number()` alone would not reject the way `null`/'' would.
function hasAptitudeScore(value) {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'string' && value.trim() !== '') return Number.isFinite(Number(value))
  return false
}

// rows: student rows, each optionally carrying `aptitudeScores`.
// Returns the ordered section keys scored by at least one row.
function resolveAptitudeSectionKeys(rows = []) {
  return APTITUDE_SECTION_ORDER.filter(key =>
    (rows || []).some(row => hasAptitudeScore(row?.aptitudeScores?.[key]))
  )
}

// Is this report an aptitude report? Scores answer it when there are any, but
// a part with none — an unscored drop-off, a candidate who never started — has
// neither `aptitudeScores` nor aptitude `sectionScores`, and the drawer used to
// fall through to the language branch and draw an Aptitude attempt as
// Reading/Listening/Speaking. The assessment's own type is the last word, the
// same way the behaviour check already reads it.
//
// normalizedType: the lower-cased assessment type of the report on screen
// (the Mix & Match part's type when a float part is open).
function isAptitudeReport(student, normalizedType = '') {
  if (student?.aptitudeScores && typeof student.aptitudeScores === 'object') return true
  if (student?.sectionScores && typeof student.sectionScores === 'object') {
    const aptitudeKeys = ['critical', 'quantitative', 'logical']
    if (Object.keys(student.sectionScores).some(key => aptitudeKeys.includes(key.toLowerCase()))) {
      return true
    }
  }
  return String(normalizedType).toLowerCase() === 'aptitude'
}

module.exports = {
  APTITUDE_SECTION_LABELS,
  APTITUDE_SECTION_ORDER,
  hasAptitudeScore,
  isAptitudeReport,
  resolveAptitudeSectionKeys,
}
